import { roundToPlaces } from './utilities';

export interface TankTransferLimits {
    // fuel the source tank can give up
    available: number;
    // 98% of the destination tank's volume, by weight
    capacity98: number | null;
    // headroom left in the destination tank
    remainingCapacity: number | null;
    // the lower of the two, never below zero
    maxQuantity: number;
}

interface TankBlendSpecs {
    sulphur: number | null;
    lcv: number | null;
    density: number | null;
}

const toNumber = (value): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
};

// once a tank has been through the BDN process its live figures live on new_bdn
const getEffectiveTank = (tank) => (tank && tank.new_bdn ? tank.new_bdn : tank);

/**
 * Sulphur, LCV and density as they were before anything in this report touched the tank.
 * Blending from these rather than from the live values keeps the result idempotent: running the
 * blend again for the same transfer must land on the same numbers instead of walking them further
 * towards the source tank every time.
 */
export const getTankBlendBaseline = (tank): TankBlendSpecs => {
    const effective = getEffectiveTank(tank) || {};

    return {
        sulphur: tank?.sulphur_before_bunkering ?? effective.sulphur_after_bunkering ?? null,
        lcv: tank?.lcv_before_bunkering ?? effective.lcv ?? null,
        density: tank?.density_before_bunkering ?? effective.density ?? null,
    };
};

/**
 * Weighted average of what the destination tank already holds and what is being transferred in.
 * Returns null when there is nothing to blend, in which case the destination should be left on its
 * baseline.
 */
export const getBlendedTankSpecs = (fromTank, toTank, quantity): TankBlendSpecs => {
    if (!fromTank || !toTank) return null;

    const amount = toNumber(toTank.amount);
    const transferred = toNumber(quantity);
    const total = amount + transferred;
    if (total <= 0) return null;

    const from = getTankBlendBaseline(fromTank);
    const to = getTankBlendBaseline(toTank);
    const blend = (toValue, fromValue) => {
        if (toValue == null && fromValue == null) return null;
        return roundToPlaces((amount * toNumber(toValue) + transferred * toNumber(fromValue)) / total, 2);
    };

    return {
        sulphur: blend(to.sulphur, from.sulphur),
        lcv: blend(to.lcv, from.lcv),
        density: blend(to.density, from.density),
    };
};

/**
 * The amount the tank would hold if this transfer row were blank.
 *
 * In BDN reporting the tank amount already has this transfer applied - written in by bdnProcess for
 * a calculated tank, or resounded by the crew for an in_use one - so it must be unwound before use as
 * a limit. Fuel based reporting has no bdnProcess, so its soundings are independent and used as-is.
 */
export const getAmountWithoutTransfer = (scope, tankId, transfer): number => {
    const effective = getEffectiveTank(scope?.report?.stock?.tanks?.[tankId]);
    const amount = toNumber(effective?.amount);

    if (!scope?.report?.bdn_based_reporting) return amount;

    const quantity = toNumber(transfer?.quantity);
    if (tankId === transfer?.from_tank) return amount + quantity;
    if (tankId === transfer?.to_tank) return amount - quantity;

    return amount;
};

/**
 * Density the tank started the report on. The blended density moves with the quantity being typed,
 * so deriving a capacity limit from it would make the limit depend on its own answer.
 */
const getBaselineDensity = (scope, tankId): number => {
    const tank = scope?.report?.stock?.tanks?.[tankId];
    if (tank?.density_before_bunkering) return toNumber(tank.density_before_bunkering);

    const carryOverDensity = getEffectiveTank(scope?.carryOverReport?.stock?.tanks?.[tankId])?.density;
    if (carryOverDensity) return toNumber(carryOverDensity);

    return toNumber(getEffectiveTank(tank)?.density);
};

/** 98% of the tank's volume expressed by weight, or null when the vessel spec can't give us one. */
export const getTankCapacity98 = (scope, tankId): number => {
    const tankSpec = scope?.vesselSpecs?.tanks?.find(spec => spec.tank_id?.['$oid'] === tankId);
    const capacity100 = tankSpec?.volume_ratings?.find(rating => rating.percent === 100);
    if (!capacity100) return null;
    if (capacity100.unit !== 'm3') return roundToPlaces(capacity100.value * 0.98, 2);

    const density = getBaselineDensity(scope, tankId);
    if (!density) return null;

    return roundToPlaces(capacity100.value * 0.98 * (density / 1000), 2);
};

/**
 * Limits on a single tank transfer, per bug 90127: a transfer may not take more than the source
 * tank holds, nor more than the destination tank has room for. Every input is taken as it would be
 * with this transfer row blank, so the answer does not move as the user types.
 */
export const getTankTransferLimits = (scope, transfer): TankTransferLimits => {
    const tanks = scope?.report?.stock?.tanks;
    if (!tanks || !transfer?.from_tank || !transfer?.to_tank) return null;
    if (!tanks[transfer.from_tank] || !tanks[transfer.to_tank]) return null;

    const available = Math.max(0, roundToPlaces(getAmountWithoutTransfer(scope, transfer.from_tank, transfer), 2));

    const capacity98 = getTankCapacity98(scope, transfer.to_tank);
    const remainingCapacity = capacity98 === null
        ? null
        : roundToPlaces(capacity98 - getAmountWithoutTransfer(scope, transfer.to_tank, transfer), 2);

    const bound = remainingCapacity === null ? available : Math.min(available, remainingCapacity);

    return {
        available,
        capacity98,
        remainingCapacity,
        maxQuantity: roundToPlaces(Math.max(0, bound), 2),
    };
};
